import { Button } from '@/components/ui/button';
import { AccountReceivablesService, SyncInvoicesFromQuickbooksRequest } from '@/api';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetPortal,
} from '@/components/ui/sheet';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { zodResolver } from '@hookform/resolvers/zod';
import { useStore } from '@nanostores/react';
import { Loader2 } from 'lucide-react';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import PhoneInput from 'react-phone-input-2'; // Import the PhoneInput component
import 'react-phone-input-2/lib/style.css'; // PhoneInput CSS
import { Loader } from '@googlemaps/js-api-loader';
import { useQueryClient } from '@tanstack/react-query';
import { Calendar } from '@/components/ui/calendar';
import { format } from 'date-fns';


interface Props {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

// Define the form schema
const formSchema = z.object({
  syncDate: z.date({
    required_error: "Please select a date",
  }),
});

type FormValues = z.infer<typeof formSchema>;

export function QuickbooksSyncDrawer({ open, setOpen }: Props) {
  const queryClient = useQueryClient();
  const org = useStore($org);
  const po = useStore($portfolio);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      syncDate: new Date(),
    },
  });

  async function onSubmit(data: FormValues) {
    try {
      toast.info('Starting sync...');
      const response = await syncInvoicesFromQuickbooks(po?.portfolio.id ?? '', data.syncDate, org?.id ?? '');
      if (response.succeeded) {
        toast.success('Sync successful');
        setOpen(false);
      } else {
        toast.error('Failed to start sync');
      }
    } catch (error) {
      console.error('Failed to sync:', error);
      toast.error('Failed to start sync');
    }
  }

  return (  
    <Sheet 
      open={open} 
      onOpenChange={setOpen}
    >
      <SheetContent className="overflow-y-auto sm:max-w-[600px]">
        <SheetHeader>
          <SheetTitle>Sync from Quickbooks</SheetTitle>
          <SheetDescription>
            Sync your account receivables from Quickbooks.
          </SheetDescription>
        </SheetHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
          <FormField
              control={form.control}
              name="syncDate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Select a date to retrieve invoices with a due date before this date</FormLabel>
                  <br />
                  <FormControl>
                  <Input
                      type="date" // Use the native HTML date picker
                      value={field.value ? field.value.toISOString().split('T')[0] : ''} // Format the date for the input
                      onChange={(e) => {
                        const value = e.target.value;
                        field.onChange(value ? new Date(value) : null); // Convert to Date object
                      }}
                    />   
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <SheetFooter>
              <Button type="submit">Start Sync</Button>
            </SheetFooter>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  );
}

